/**
 * WebAuthn helpers for sign-in and step-up (approvals, dangerous actions).
 * With the mock API on, no authenticator is involved: we wait briefly and return a placeholder
 * assertion that the mock accepts, so the flow can be clicked through on any machine.
 */
import type { Schemas } from "@/lib/api/types";
import { config } from "./config";

type RequestOptions = Schemas["WebAuthnRequestOptions"];
type Credential = Schemas["WebAuthnCredential"];

const b64urlToBuf = (s: string): ArrayBuffer => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};
const bufToB64url = (b: ArrayBuffer | null | undefined): string => {
  if (!b) return "";
  let s = "";
  for (const byte of new Uint8Array(b)) s += String.fromCharCode(byte);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window;
}

export class PasskeyCancelled extends Error {
  constructor() {
    super("Passkey check was cancelled.");
    this.name = "PasskeyCancelled";
  }
}

/** Ask the browser for a passkey assertion. Throws PasskeyCancelled if the person dismisses the prompt. */
export async function getAssertion(options: RequestOptions | undefined): Promise<Credential> {
  if (config.mock || !options) {
    await new Promise((r) => setTimeout(r, 700));
    return { id: "mock-credential", rawId: "bW9jaw", type: "public-key", response: { clientDataJSON: "e30", authenticatorData: "", signature: "" } };
  }
  const o = options as unknown as {
    challenge: string;
    rpId?: string;
    timeout?: number;
    userVerification?: UserVerificationRequirement;
    allowCredentials?: { id: string; transports?: string[] }[];
  };
  let cred: PublicKeyCredential | null;
  try {
    cred = (await navigator.credentials.get({
      publicKey: {
        challenge: b64urlToBuf(o.challenge),
        rpId: o.rpId,
        timeout: o.timeout,
        userVerification: o.userVerification ?? "required",
        allowCredentials: o.allowCredentials?.map((c) => ({ id: b64urlToBuf(c.id), type: "public-key" as const, transports: c.transports as AuthenticatorTransport[] | undefined })),
      },
    })) as PublicKeyCredential | null;
  } catch (e) {
    if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "AbortError")) throw new PasskeyCancelled();
    throw e;
  }
  if (!cred) throw new PasskeyCancelled();
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: "public-key",
    authenticatorAttachment: cred.authenticatorAttachment,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      authenticatorData: bufToB64url(r.authenticatorData),
      signature: bufToB64url(r.signature),
      userHandle: bufToB64url(r.userHandle),
    },
    clientExtensionResults: {},
  };
}

type CreationOptions = Schemas["WebAuthnCreationOptions"];

/** Create a new passkey (sign-up, or adding a device). Throws PasskeyCancelled if dismissed. */
export async function createCredential(options: CreationOptions | undefined): Promise<Credential> {
  if (config.mock || !options) {
    await new Promise((r) => setTimeout(r, 800));
    return { id: "mock-new-credential", rawId: "bmV3", type: "public-key", response: { clientDataJSON: "e30", attestationObject: "" } };
  }
  const o = options as unknown as {
    challenge: string;
    rp: { id?: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: { type: "public-key"; alg: number }[];
    timeout?: number;
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
  };
  let cred: PublicKeyCredential | null;
  try {
    cred = (await navigator.credentials.create({
      publicKey: {
        challenge: b64urlToBuf(o.challenge),
        rp: o.rp,
        user: { id: b64urlToBuf(o.user.id), name: o.user.name, displayName: o.user.displayName },
        pubKeyCredParams: o.pubKeyCredParams,
        timeout: o.timeout,
        authenticatorSelection: o.authenticatorSelection ?? { residentKey: "required", userVerification: "required" },
        attestation: o.attestation ?? "none",
      },
    })) as PublicKeyCredential | null;
  } catch (e) {
    if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "AbortError")) throw new PasskeyCancelled();
    throw e;
  }
  if (!cred) throw new PasskeyCancelled();
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: "public-key",
    authenticatorAttachment: cred.authenticatorAttachment,
    response: { clientDataJSON: bufToB64url(r.clientDataJSON), attestationObject: bufToB64url(r.attestationObject), transports: r.getTransports?.() ?? [] },
    clientExtensionResults: {},
  };
}
